import { useState } from "react";
import { format, parseISO } from "date-fns";
import { AlertCircle, AudioLines, Columns3 } from "lucide-react";
import { toast } from "sonner";
import type { DateRange } from "react-day-picker";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { DateRangeFilter } from "@/components/ui/date-range-filter";
import { FilterMenu } from "@/components/ui/filter-menu";
import { SearchInput } from "@/components/ui/search-input";
import { ConsultationRecordingDetailsPanel } from "@/components/settings/ConsultationRecordingDetailsPanel";
import { ConsultationRecordingsTable } from "@/components/settings/ConsultationRecordingsTable";
import {
  COLUMN_LABELS,
  type ConsultationRecordingProcessingState,
  type RecordingStatus,
  type SortDirection,
  type SortKey,
} from "@/components/settings/consultation-recordings-table.types";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useConsultationRecordingPlayback,
  useConsultationRecordingProviders,
  useConsultationRecordingReprocess,
  useConsultationRecordings,
  useConsultationRecordingSegments,
  useConsultationRecordingTranscript,
} from "@/hooks/useConsultationRecordings";
import { useDebounce } from "@/hooks/use-debounce";
import { cn } from "@/lib/utils";

interface DateRangeState {
  start: string;
  end: string;
}

const PAGE_SIZE = 20;

const STATUS_OPTIONS: Array<{ label: string; value: RecordingStatus }> = [
  { label: "Ready", value: "Ready" },
  { label: "Processing", value: "Processing" },
  { label: "Partial", value: "Partial" },
  { label: "Failed", value: "Failed" },
];

const FILTER_TRIGGER =
  "border-border/70 bg-background px-3 shadow-none hover:bg-accent/20";
const FILTER_CONTENT = "border-border/70 bg-popover shadow-md";

const EMPTY_DATE_RANGE: DateRangeState = { start: "", end: "" };

const getDateRangeValue = (dateRange: DateRangeState): DateRange | undefined =>
  dateRange.start && dateRange.end
    ? { from: parseISO(dateRange.start), to: parseISO(dateRange.end) }
    : undefined;

const toApiStatus = (status: RecordingStatus | "All") => {
  if (status === "All") return undefined;
  return status.toLowerCase() as "ready" | "processing" | "partial" | "failed";
};

const toApiSortKey = (sortKey: SortKey) => {
  switch (sortKey) {
    case "date":
    case "startTime":
      return "start_time";
    case "patient":
      return "patient_name";
    case "provider":
      return "provider_name";
    case "segments":
      return "segment_count";
    case "totalDuration":
      return "total_duration";
    case "status":
      return "status";
    default:
      return "start_time";
  }
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: string }).message;
    if (typeof message === "string" && message.trim()) {
      return message;
    }
  }
  return fallback;
};

const TableSkeleton = ({ columnCount }: { columnCount: number }) => (
  <div className="space-y-2">
    {Array.from({ length: 5 }).map((_, rowIndex) => (
      <div
        key={`skeleton-row-${rowIndex + 1}`}
        className="grid gap-4 rounded-xl border border-border/60 bg-card px-4 py-4"
        style={{
          gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
        }}
      >
        {Array.from({ length: columnCount }).map((__, columnIndex) => (
          <Skeleton
            key={`skeleton-cell-${rowIndex + 1}-${columnIndex + 1}`}
            className={cn("h-5", columnIndex === 0 ? "w-24" : "w-full")}
          />
        ))}
      </div>
    ))}
  </div>
);

export const ConsultationRecordings = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<RecordingStatus | "All">(
    "All"
  );
  const [dateRange, setDateRange] = useState<DateRangeState>(EMPTY_DATE_RANGE);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(
    null
  );
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRecordingId, setSelectedRecordingId] = useState<string | null>(
    null
  );
  const [activePlaybackId, setActivePlaybackId] = useState<string | null>(null);
  const [processingStateById, setProcessingStateById] = useState<
    Record<string, ConsultationRecordingProcessingState>
  >({});
  const [visibleColumns, setVisibleColumns] = useState<
    Record<SortKey, boolean>
  >({
    date: true,
    startTime: true,
    patient: true,
    provider: true,
    segments: true,
    totalDuration: true,
    status: true,
  });

  const debouncedSearchQuery = useDebounce(searchQuery, 400);

  const {
    data: recordingsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useConsultationRecordings({
    page: currentPage,
    page_size: PAGE_SIZE,
    search: debouncedSearchQuery.trim() || undefined,
    status: toApiStatus(selectedStatus),
    provider_id: selectedProviderId || undefined,
    date_from: dateRange.start || undefined,
    date_to: dateRange.end || undefined,
    sort_by: toApiSortKey(sortKey),
    sort_order: sortDirection,
  });

  const { data: providerOptions = [] } = useConsultationRecordingProviders();

  const {
    data: selectedSegments = [],
    isLoading: isSelectedSegmentsLoading,
    error: selectedSegmentsError,
  } = useConsultationRecordingSegments(
    selectedRecordingId,
    Boolean(selectedRecordingId)
  );

  const {
    data: playbackData,
    isLoading: isPlaybackLoading,
    error: playbackError,
  } = useConsultationRecordingPlayback(
    activePlaybackId,
    Boolean(activePlaybackId)
  );

  const {
    data: transcriptData,
    isLoading: isTranscriptLoading,
    error: transcriptError,
  } = useConsultationRecordingTranscript(
    selectedRecordingId,
    Boolean(selectedRecordingId)
  );

  const reprocessMutation = useConsultationRecordingReprocess();

  const recordings = recordingsData?.recordings ?? [];
  const pagination = recordingsData?.pagination ?? {
    page: 1,
    pageSize: PAGE_SIZE,
    totalCount: 0,
    totalPages: 1,
  };

  const providerFilterOptions = providerOptions.map((p) => ({
    label: p.name,
    value: p.id,
  }));

  const selectedRecording =
    recordings.find((r) => r.id === selectedRecordingId) ?? null;

  const hasFilteredResults = recordings.length > 0;
  const hasActiveFilters =
    searchQuery.trim().length > 0 ||
    selectedStatus !== "All" ||
    selectedProviderId !== null ||
    dateRange.start !== "" ||
    dateRange.end !== "";

  const playbackStateLabel = playbackData?.status
    ? `Playback status: ${playbackData.status}`
    : null;
  const hasTranscriptFailure =
    transcriptData?.status?.toLowerCase() === "failed";
  const shouldShowReprocessAction =
    (Boolean(activePlaybackId) &&
      playbackData?.status === "failed" &&
      !playbackData?.url &&
      !playbackError) ||
    hasTranscriptFailure;
  const activeProcessingState = selectedRecordingId
    ? (processingStateById[selectedRecordingId] ?? null)
    : null;

  const handleSort = (key: SortKey) => {
    setCurrentPage(1);
    if (sortKey === key) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection(key === "date" ? "desc" : "asc");
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedStatus("All");
    setSelectedProviderId(null);
    setDateRange(EMPTY_DATE_RANGE);
    setCurrentPage(1);
  };

  const handleDateRangeSelect = (range: DateRange | undefined) => {
    setCurrentPage(1);
    if (!range?.from) {
      setDateRange({ start: "", end: "" });
      return;
    }
    setDateRange({
      start: format(range.from, "yyyy-MM-dd"),
      end: format(range.to ?? range.from, "yyyy-MM-dd"),
    });
  };

  const handleSelectRecording = (recordingId: string) => {
    setSelectedRecordingId(recordingId);
    if (activePlaybackId && activePlaybackId !== recordingId) {
      setActivePlaybackId(null);
    }
  };

  const handleClosePanel = () => {
    setSelectedRecordingId(null);
    setActivePlaybackId(null);
  };

  const handlePlayClick = () => {
    if (!selectedRecordingId) return;
    setActivePlaybackId((current) =>
      current === selectedRecordingId ? null : selectedRecordingId
    );
  };

  const handleReprocessClick = async () => {
    if (!selectedRecordingId) return;
    try {
      const response = await reprocessMutation.mutateAsync(selectedRecordingId);
      setProcessingStateById((current) => ({
        ...current,
        [selectedRecordingId]: response,
      }));
      toast.success("Recording reprocess started.");
    } catch (error) {
      toast.error(
        getErrorMessage(error, "Recording reprocess could not be started.")
      );
    }
  };

  return (
    <div className="flex h-full min-h-0 gap-3">
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-3xl border-border/60 bg-card shadow-sm">
        <CardHeader className="shrink-0 gap-4 border-b border-border/60 p-5">
          <h1 className="z-h2 text-foreground">Consultation Recordings</h1>

          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="w-full xl:max-w-sm">
              <SearchInput
                value={searchQuery}
                onChange={(event) => {
                  setSearchQuery(event.target.value);
                  setCurrentPage(1);
                }}
                onClear={() => {
                  setSearchQuery("");
                  setCurrentPage(1);
                }}
                placeholder="Search patient or provider..."
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <FilterMenu
                label="Status"
                options={STATUS_OPTIONS}
                value={selectedStatus === "All" ? "all" : selectedStatus}
                onValueChange={(value) => {
                  setSelectedStatus(
                    value === "all" ? "All" : (value as RecordingStatus)
                  );
                  setCurrentPage(1);
                }}
                triggerClassName={FILTER_TRIGGER}
                contentClassName={FILTER_CONTENT}
              />

              {providerFilterOptions.length > 0 ? (
                <FilterMenu
                  label="Provider"
                  options={providerFilterOptions}
                  value={selectedProviderId ?? "all"}
                  onValueChange={(value) => {
                    setSelectedProviderId(value === "all" ? null : value);
                    setCurrentPage(1);
                  }}
                  triggerClassName={FILTER_TRIGGER}
                  contentClassName={FILTER_CONTENT}
                />
              ) : null}

              <DateRangeFilter
                value={getDateRangeValue(dateRange)}
                onChange={handleDateRangeSelect}
                placeholder="Date range"
                triggerClassName={`${FILTER_TRIGGER} font-normal`}
                contentClassName={FILTER_CONTENT}
                align="end"
                disabled={{ after: new Date() }}
              />

              <DropdownMenu
                open={columnsMenuOpen}
                onOpenChange={setColumnsMenuOpen}
              >
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={FILTER_TRIGGER}
                  >
                    <Columns3 className="mr-1.5 h-4 w-4" />
                    Columns
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {(Object.keys(COLUMN_LABELS) as SortKey[]).map(
                    (columnKey) => (
                      <DropdownMenuCheckboxItem
                        key={columnKey}
                        checked={visibleColumns[columnKey]}
                        onSelect={(event) => event.preventDefault()}
                        onCheckedChange={(checked) =>
                          setVisibleColumns((current) => ({
                            ...current,
                            [columnKey]: Boolean(checked),
                          }))
                        }
                      >
                        {COLUMN_LABELS[columnKey]}
                      </DropdownMenuCheckboxItem>
                    )
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {hasActiveFilters ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={clearFilters}
                >
                  Clear filters
                </Button>
              ) : null}
            </div>
          </div>

          {hasActiveFilters ? (
            <p className="z-caption text-muted-foreground">
              Showing {pagination.totalCount} matching recording
              {pagination.totalCount === 1 ? "" : "s"}.
            </p>
          ) : null}
        </CardHeader>

        <CardContent className="flex flex-1 min-h-0 flex-col p-0">
          {error ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <AlertCircle className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <h2 className="z-h3 text-foreground">
                  Unable to load consultation recordings
                </h2>
                <p className="max-w-md z-secondary text-muted-foreground">
                  {getErrorMessage(
                    error,
                    "The recordings service did not return data."
                  )}
                </p>
              </div>
              <Button className="mt-2" onClick={() => void refetch()}>
                Retry
              </Button>
            </div>
          ) : isLoading ? (
            <div className="flex-1 space-y-4 p-5">
              <TableSkeleton
                columnCount={
                  Object.values(visibleColumns).filter(Boolean).length + 1
                }
              />
            </div>
          ) : !hasFilteredResults ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <AudioLines className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <h2 className="z-h3 text-foreground">
                  {hasActiveFilters
                    ? "No recordings match your filters"
                    : "No consultation recordings yet"}
                </h2>
                <p className="max-w-sm z-secondary text-muted-foreground">
                  {hasActiveFilters
                    ? "Try adjusting your search or filters."
                    : "Recordings will appear here once video consultations begin."}
                </p>
              </div>
              {hasActiveFilters ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-1"
                  onClick={clearFilters}
                >
                  Clear filters
                </Button>
              ) : null}
            </div>
          ) : (
            <ConsultationRecordingsTable
              recordings={recordings}
              visibleColumns={visibleColumns}
              sortKey={sortKey}
              sortDirection={sortDirection}
              selectedRecordingId={selectedRecordingId}
              searchQuery={debouncedSearchQuery}
              currentPage={pagination.page}
              pageSize={pagination.pageSize}
              totalPages={pagination.totalPages}
              totalRecords={pagination.totalCount}
              onSort={handleSort}
              onSelectRecording={handleSelectRecording}
              onPreviousPage={() =>
                setCurrentPage((page) => Math.max(1, page - 1))
              }
              onNextPage={() =>
                setCurrentPage((page) =>
                  Math.min(pagination.totalPages, page + 1)
                )
              }
            />
          )}

          {isFetching && !isLoading ? (
            <div className="border-t border-border/60 px-5 py-2 z-caption text-muted-foreground">
              Refreshing recordings...
            </div>
          ) : null}
        </CardContent>
      </Card>

      {selectedRecording ? (
        <ConsultationRecordingDetailsPanel
          recording={selectedRecording}
          onClose={handleClosePanel}
          isPlaybackOpen={activePlaybackId === selectedRecording.id}
          isPlaybackLoading={
            isPlaybackLoading && activePlaybackId === selectedRecording.id
          }
          playbackUrl={playbackData?.url || null}
          playbackStateLabel={playbackStateLabel}
          playbackError={
            playbackError
              ? getErrorMessage(
                  playbackError,
                  "Playback could not be loaded for this recording."
                )
              : null
          }
          transcript={transcriptData ?? null}
          isTranscriptLoading={isTranscriptLoading}
          transcriptError={
            transcriptError
              ? getErrorMessage(
                  transcriptError,
                  "Transcript could not be loaded for this recording."
                )
              : null
          }
          segments={selectedSegments}
          isSegmentsLoading={isSelectedSegmentsLoading}
          segmentsError={
            selectedSegmentsError
              ? getErrorMessage(
                  selectedSegmentsError,
                  "Segment details could not be loaded."
                )
              : null
          }
          activeProcessingState={activeProcessingState}
          isReprocessing={
            reprocessMutation.isPending &&
            reprocessMutation.variables === selectedRecording.id
          }
          shouldShowReprocessAction={shouldShowReprocessAction}
          onPlayClick={handlePlayClick}
          onReprocessClick={() => void handleReprocessClick()}
        />
      ) : null}
    </div>
  );
};

export default ConsultationRecordings;
